<template>
  <div v-if="!isLoading" class="resource-tagging edit">
    <cv-inline-notification
      v-if="isAdaptiveSection"
      kind="info"
      :sub-title="t('section_metadata_learning_outcomes_banner_message')"
      low-contrast
      hide-close-button
    />
    <dt v-if="showTagsMessage" class="section-tags-description">
      {{ t("resource_tagging_section_tags_message") }}
    </dt>
    <dt class="field-label">{{ t("resource_tagging_subject_and_level_label") }}</dt>
    <resource-tagging-subject-level
      v-for="subjectLevelTagging in subjectLevelTaggings"
      :key="subjectLevelTagging.tagId"
      :subject-level-tagging="subjectLevelTagging"
      :resource-uuid="resourceUuid"
      :is-adaptive-section="isAdaptiveSection"
      :selection-kind="selectionKind"
      @delete-required-content-map="deleteRequiredContentMap"
      @subject-selected="handleSubjectSelection"
    />
    <cv-button-set v-if="!isAdaptiveSection" class="actions">
      <cv-button
        type="button"
        kind="tertiary"
        size="field"
        class="add-subject-level-tag"
        @click="handleAddSubjectLevel"
      >
        <svg-icon name="Plus24" kind="action" />
        <span>{{ t("resource_tagging_button_add_subject_level") }}</span>
      </cv-button>
    </cv-button-set>

    <dl class="field-set content-map-learning-outcome">
      <dt class="field-label">{{ contentMapAndTopicTitle }}</dt>
      <dd class="field-input">
        <AccordionWrapper v-if="isEditTagReady" ref="acc2" :kind="accordionKind" class="tagging">
          <resource-tagging-item-edit
            v-for="(contentMapTagging, index) in contentMapTaggings"
            :key="contentMapTagging.tagId"
            :open="acc2State[index]"
            :resource-tagging="contentMapTagging"
            :resource-type="resourceType"
            :resource-uuid="resourceUuid"
            :allow-multiple-select-topic="allowMultipleSelectTopic"
            :is-adaptive-section="isAdaptiveSection"
            :content-map-warning-context="contentMapWarningContext"
            data-testid="resource-tagging-item-edit"
            @remove-tagging="handleRemoveContentMapTagging"
            @cv:change="acc2Change($event, index, contentMapTagging.tagId)"
          />
        </AccordionWrapper>
      </dd>
    </dl>
    <message-component v-if="hasLessonTagError" class="error submit-validation-error">
      {{ getLessonTagError }}
    </message-component>
    <cv-button-set v-if="isEditTagReady" class="actions">
      <cv-button
        v-if="showLessonActionButton"
        type="button"
        kind="tertiary"
        size="field"
        class="add-lesson-tag"
        :disabled="disableAddContentMap"
        @click="handleAddResourceTagging"
      >
        <svg-icon name="Plus24" kind="action" />
        <span>{{ t("resource_tagging_button_add_cm_topic") }}</span>
      </cv-button>
      <cv-button
        v-if="showSectionActionButton && !isAdaptiveSection"
        type="button"
        kind="tertiary"
        size="field"
        class="add-section-tag"
        :disabled="disableAddContentMap"
        @click="handleAddResourceTagging"
      >
        <svg-icon name="Plus24" kind="action" />
        <span>{{ t("resource_tagging_button_add_cm_topic") }}</span>
      </cv-button>
      <cv-button
        v-if="showAddContentMapAndTopicButton"
        type="button"
        kind="tertiary"
        size="field"
        class="add-question-tag"
        :disabled="disableAddContentMap"
        @click="handleAddResourceTagging"
      >
        <svg-icon name="Plus24" kind="action" />
        <span>{{ t("resource_tagging_button_add_cm_topic") }}</span>
      </cv-button>
    </cv-button-set>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import ResourceTaggingItemEdit from "@modules/editor/components/resource-taggings/ResourceTaggingItemEdit.vue";
import taggingStore from "@modules/resource-tagging-selection/components/ResourceTaggingSelectionStore";
import resourceTaggingsStore from "@modules/editor/components/resource-taggings/ResourceTaggingsStore";
import { NAMESPACE, RESOURCE_TAGGING_RESOURCE_TYPE } from "@modules/editor/service/editor.constant";
import { isDefined, isEmpty } from "@/utils/var.check";
import submitLessonValidationStore from "@modules/editor/components/lesson/SubmitLessonValidationStore";
import ROLE from "@configs/role";
import ObjectUtils from "@utils/object.utils";
import ResourceTaggingSubjectLevel from "@modules/resource-tagging-selection/components/ResourceTaggingSubjectLevel.vue";
import { useDirty } from "@/plugins/common/composables/page-dirty";
import { useErrorHandler } from "@/plugins/common/composables/error";
import RandomUtils from "@utils/random.utils.ts";
import type { ContentMapWarningContext } from "@modules/resource-tagging-selection/service/resource.tagging.selection.constant.ts";

// Props
interface Props {
  resourceUuid: string;
  resourceType: string;
  resourceStatus?: string;
  allowMultipleSelectTopic?: boolean;
  isAdaptiveSection?: boolean;
  showSectionTagsMessage?: boolean;
  selectionKind?: string;
  accordionKind?: string;
  skipFetch?: boolean;
  contentMapWarningContext?: ContentMapWarningContext;
}

const props = withDefaults(defineProps<Props>(), {
  resourceStatus: undefined,
  allowMultipleSelectTopic: true,
  isAdaptiveSection: false,
  showSectionTagsMessage: false,
  selectionKind: "white",
  accordionKind: "",
  skipFetch: false,
  contentMapWarningContext: undefined,
});

// Emits
const emit = defineEmits<{
  loaded: [];
}>();

// Composables
const processingId = computed(() => {
  return id.value;
});
const store = useStore();
const { t } = useI18n();
const { setDirty } = useDirty(processingId);
const { passiveApiFailHandler } = useErrorHandler();

// Data
const isLoading = ref(true);
const isTagged = ref(false);
const isEditTagReady = ref(false);
const hiddenResourceTaggings = ref<Record<string, unknown>>({});
const acc2ItemCount = ref(0);
const acc2OpenedId = ref<string | null>(null);
const acc2State = ref<boolean[]>([]);
const contentMappingUuidsCacheSet = ref(new Set());

// Computed

const hasRole = computed(() => store.getters[`${NAMESPACE.AUTH}/hasRole`]);

const isContentApprover = computed(() => {
  return hasRole.value(ROLE.CONTENT_APPROVER);
});

const isContentOfficer = computed(() => {
  return hasRole.value(ROLE.CONTENT_OFFICER);
});

const validationErrors = computed<Record<string, unknown>>(() => {
  if (!submitLessonValidationStore) return {};
  return Object.assign({}, submitLessonValidationStore.getSubmissionValidationErrors());
});

const hasLessonTagError = computed(() => {
  return (
    validationErrors.value &&
    (!isEmpty(validationErrors.value.contentMapNodePath) ||
      !isEmpty(validationErrors.value.resourceTaggingError))
  );
});

const getLessonTagError = computed(() => {
  return t("lesson_submit_validation_lesson_tag_error_label");
});

const id = computed(() => {
  return props.resourceUuid;
});

const showLessonActionButton = computed(() => {
  return props.resourceType.toUpperCase() === RESOURCE_TAGGING_RESOURCE_TYPE.LESSON;
});

const showSectionActionButton = computed(() => {
  const isSection =
    props.resourceType.toUpperCase() === RESOURCE_TAGGING_RESOURCE_TYPE.LESSON_ACTIVITY;
  return isSection && (!props.isAdaptiveSection || contentMapTaggings.value.length == 0);
});

const showAddContentMapAndTopicButton = computed(() => {
  return (
    props.resourceType.toUpperCase() === RESOURCE_TAGGING_RESOURCE_TYPE.QUESTION ||
    props.resourceType.toUpperCase() === RESOURCE_TAGGING_RESOURCE_TYPE.RUBRIC
  );
});

const hasCcpmRole = computed(() => {
  return isContentApprover.value || isContentOfficer.value;
});

const subjectLevelTaggings = computed(() => {
  return resourceTaggingsStore.getSubjectAndLevelTaggingsByResource(props.resourceUuid);
});

const contentMapTaggings = computed(() => {
  return resourceTaggingsStore.getContentMapTaggingsByResource(props.resourceUuid);
});

const disableAddContentMap = computed(() => {
  return (
    (!props.allowMultipleSelectTopic &&
      !isEmpty(contentMapTaggings.value) &&
      contentMapTaggings.value.length > 0) ||
    subjectLevelTaggings.value.every(
      (tagging) => isEmpty(tagging.levelAndStream) || isEmpty(tagging.levelAndStream.id),
    )
  );
});

const contentMapAndTopicTitle = computed(() => {
  if (props.isAdaptiveSection) {
    return t("adaptive_learning_content_map_and_learning_outcome_label");
  } else {
    return t("resource_tagging_cm_topic_label");
  }
});

const showTagsMessage = computed(() => {
  return props.showSectionTagsMessage && !props.isAdaptiveSection;
});

// Methods
const deleteRequiredContentMap = function (subjectLevelTagging) {
  contentMapTaggings.value.forEach((contentMapTagging) => {
    if (isEmpty(contentMapTagging.contentMapMetadata)) {
      return;
    }
    const cmUuid = contentMapTagging.contentMapMetadata.uuid;
    const cmMetadata = taggingStore.getContentMap(cmUuid);
    const cmSubject = cmMetadata.subjects[subjectLevelTagging.subject.id];
    if (isEmpty(cmSubject)) {
      return;
    }
    const cmSubjectLevel = cmSubject.levelAndStreams[subjectLevelTagging.levelAndStream.id];
    if (isEmpty(cmSubjectLevel)) {
      return;
    }
    handleRemoveContentMapTagging(contentMapTagging.tagId);
  });
};

const handleAddSubjectLevel = function () {
  addDefaultSubjectLevelTagging();
};

const acc2Change = function (ev, changedIndex: number, changedId: string) {
  acc2State.value = acc2State.value.map((_item, index) => {
    return index === changedIndex && ev.dataOpen;
  });
  if (ev.dataOpen) {
    acc2OpenedId.value = changedId;
  } else {
    acc2OpenedId.value = null;
  }
};

const handleAddResourceTagging = function () {
  addDefaultContentMapTagging();
};

const handleRemoveContentMapTagging = function (tagId: string) {
  const hiddenResourceTaggingsLength = !isEmpty(hiddenResourceTaggings.value)
    ? Object.keys(hiddenResourceTaggings.value).length
    : 0;
  const editingTaggingLength = !isEmpty(contentMapTaggings.value)
    ? contentMapTaggings.value.length
    : 0;

  if (
    !isEmpty(contentMapTaggings.value) &&
    editingTaggingLength - hiddenResourceTaggingsLength === 1
  ) {
    addDefaultContentMapTagging();
  }

  setDirty(processingId.value);

  resourceTaggingsStore.removeContentMapTagging(tagId);
};

const addDefaultSubjectLevelTagging = function () {
  const defaultSubjectLevelTaggingToAdd: Record<string, unknown> = {};
  const tagId = RandomUtils.randomId();

  defaultSubjectLevelTaggingToAdd.tagId = props.resourceUuid.concat("_", tagId);
  defaultSubjectLevelTaggingToAdd.levelAndStream = { id: "" };
  defaultSubjectLevelTaggingToAdd.subject = { id: "" };
  defaultSubjectLevelTaggingToAdd.contentMapMetadata = null;
  resourceTaggingsStore.addSubjectLevelTagging(defaultSubjectLevelTaggingToAdd);
};

const addDefaultContentMapTagging = function () {
  const defaultContentMapTaggingToAdd: Record<string, unknown> = {};
  const tagId = RandomUtils.randomId();

  // default open for new added tagging (not first default tagging)
  const hiddenResourceTaggingsLength = !isEmpty(hiddenResourceTaggings.value)
    ? Object.keys(hiddenResourceTaggings.value).length
    : 0;
  const editingTaggingLength = !isEmpty(contentMapTaggings.value)
    ? contentMapTaggings.value.length
    : 0;

  if (editingTaggingLength - hiddenResourceTaggingsLength >= 1) {
    acc2OpenedId.value = tagId;
  }

  defaultContentMapTaggingToAdd.tagId = props.resourceUuid.concat("_", tagId);
  defaultContentMapTaggingToAdd.contentMapMetadata = {};
  resourceTaggingsStore.addContentMapTagging(defaultContentMapTaggingToAdd);
};

const filterHiddenResourceTaggings = function (resourceTaggings) {
  if (hasCcpmRole.value) {
    hiddenResourceTaggings.value = resourceTaggings.filter((resourceTagging) => {
      return (
        !isEmpty(resourceTagging.contentMapMetadata) && resourceTagging.contentMapMetadata.expired
      );
    });
  } else {
    hiddenResourceTaggings.value = resourceTaggings.filter((resourceTagging) => {
      return (
        !isEmpty(resourceTagging.contentMapMetadata) &&
        (resourceTagging.contentMapMetadata.expired ||
          (!resourceTagging.contentMapMetadata.started &&
            !resourceTagging.contentMapMetadata.viewableByTeachers))
      );
    });
  }
};

const handleSubjectSelection = async (subjectId) => {
  if (!subjectId) return;

  try {
    const subject = taggingStore.getSubject(subjectId.toString());

    // Check if content maps are already loaded for this subject
    const hasContentMaps =
      subject &&
      subject.levelAndStreams &&
      typeof subject.levelAndStreams === "object" &&
      Object.keys(subject.levelAndStreams).length > 0;

    if (hasContentMaps) {
      return;
    }

    await taggingStore.fetchLevelAndStreamContentMap([subjectId]);
  } catch (error) {
    passiveApiFailHandler(error);
  }
};

// Watch
watch(
  contentMapTaggings,
  function () {
    if (!isEmpty(contentMapTaggings.value)) {
      isTagged.value = true;

      // Check for changes in content map taggings, if there is no change, exit
      const newContentMapTaggingsUuidsSet = new Set();
      contentMapTaggings.value.forEach((tagging) => {
        if (tagging.contentMapMetadata?.uuid)
          newContentMapTaggingsUuidsSet.add(tagging.contentMapMetadata?.uuid);
      });

      if (
        newContentMapTaggingsUuidsSet.size !== contentMappingUuidsCacheSet.value.size ||
        newContentMapTaggingsUuidsSet.difference(contentMappingUuidsCacheSet.value).size
      ) {
        contentMappingUuidsCacheSet.value = newContentMapTaggingsUuidsSet;
      } else {
        isEditTagReady.value = true;
        return;
      }

      // clone resource taggings for filter hidden resource taggings
      const resourceTaggingsToFilterHidden = contentMapTaggings.value.map((resourceTagging) => {
        return resourceTagging;
      });
      filterHiddenResourceTaggings(resourceTaggingsToFilterHidden);

      // clone resource taggings for edit
      const resourceTaggingsToEdit = contentMapTaggings.value.map((resourceTagging) => {
        return resourceTagging;
      });
      resourceTaggingsStore.addEditingResourceTaggings(resourceTaggingsToEdit);
    }

    const hiddenResourceTaggingsLength = !isEmpty(hiddenResourceTaggings.value)
      ? Object.keys(hiddenResourceTaggings.value).length
      : 0;
    const editingTaggingLength = !isEmpty(contentMapTaggings.value)
      ? contentMapTaggings.value.length
      : 0;

    if (
      isEmpty(contentMapTaggings.value) ||
      editingTaggingLength - hiddenResourceTaggingsLength === 0
    ) {
      addDefaultContentMapTagging();
      isEditTagReady.value = true;
    }

    const newSize = contentMapTaggings.value.length;
    if (acc2ItemCount.value !== newSize) {
      acc2ItemCount.value = newSize;
      acc2State.value = contentMapTaggings.value.map((item) => acc2OpenedId.value === item.tagId);
    }
  },
  { deep: true },
);

onMounted(() => {
  //reset previous editing resource tagging
  if (!isTagged.value) {
    resourceTaggingsStore.addEditingResourceTaggings({});
  }

  const fetchResourceTaggingsPromise = props.skipFetch
    ? Promise.resolve()
    : resourceTaggingsStore.fetchResourceTaggings({
        uuid: props.resourceUuid,
        resourceType: props.resourceType.toUpperCase(),
        resourceStatus: isDefined(props.resourceStatus) ? props.resourceStatus : "APPROVED",
        includeExpired: false,
      });

  if (!props.skipFetch) {
    fetchResourceTaggingsPromise.catch(() => {
      passiveApiFailHandler();
    });
  }

  const otherSubjects = computed(() => store.state.tagging?.otherSubjects);

  const fetchCategorizedSubjectPromise = taggingStore.fetchCategorizedSubject();
  fetchCategorizedSubjectPromise.catch(() => {
    passiveApiFailHandler();
  });

  if (isEmpty(otherSubjects.value) || taggingStore.hasViewModeContentMapOnly()) {
    Promise.all([fetchResourceTaggingsPromise, fetchCategorizedSubjectPromise]).then(() => {
      const subjectIds = [];
      subjectLevelTaggings.value.forEach((tagging) => {
        if (!isEmpty(tagging.subject) && !isEmpty(tagging.subject.id)) {
          subjectIds.push(tagging.subject.id);
        }
      });
      if (subjectIds.length > 0) {
        taggingStore.fetchLevelAndStreamContentMap(subjectIds).then(() => {
          isLoading.value = false;
          emit("loaded");
        });
      } else {
        isLoading.value = false;
        emit("loaded");
      }
    });
  } else {
    const subjects = ObjectUtils.deepClone(taggingStore.getSubjects());

    Promise.all([fetchResourceTaggingsPromise, fetchCategorizedSubjectPromise]).then(() => {
      const subjectIds = [];
      subjects.value.forEach((tagging) => {
        if (!isEmpty(tagging.subject) && !isEmpty(tagging.subject.id)) {
          subjectIds.push(tagging.subject.id);
        }
      });
      if (subjectIds.length > 0) {
        taggingStore.fetchLevelAndStreamContentMap(subjectIds).then(() => {
          isLoading.value = false;
          emit("loaded");
        });
      } else {
        isLoading.value = false;
        emit("loaded");
      }
    });
  }
  if (isEmpty(contentMapTaggings.value)) {
    addDefaultContentMapTagging();
  } else if (!isEmpty(contentMapTaggings.value)) {
    isEditTagReady.value = true;
  }

  if (isEmpty(subjectLevelTaggings.value)) {
    addDefaultSubjectLevelTagging();
  }
});

onUnmounted(() => {
  resourceTaggingsStore.clearResourceTaggingsByResource(props.resourceUuid);
});
</script>
